import { useState, FormEvent, useEffect } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useCreateCategory, useUpdateCategory, useArchiveCategory, type Category } from '../lib/categories'
import { useCategories } from '../lib/categories'

interface CategoryFormProps {
  open: boolean
  onClose: () => void
  category?: Category | null
}

const CATEGORY_TYPES = [
  { value: 'income', label: 'Rendimento', icon: '💰' },
  { value: 'expense', label: 'Despesa', icon: '💸' },
] as const

// Font Awesome icon mapping (using icon names)
const ICON_OPTIONS = [
  { value: 'utensils', label: 'Alimentação', emoji: '🍽️' },
  { value: 'car', label: 'Transporte', emoji: '🚗' },
  { value: 'home', label: 'Moradia', emoji: '🏠' },
  { value: 'shopping-cart', label: 'Compras', emoji: '🛒' },
  { value: 'music', label: 'Entretenimento', emoji: '🎵' },
  { value: 'heart', label: 'Saúde', emoji: '❤️' },
  { value: 'education', label: 'Educação', emoji: '📚' },
  { value: 'briefcase', label: 'Trabalho', emoji: '💼' },
  { value: 'plane', label: 'Viagens', emoji: '✈️' },
  { value: 'gift', label: 'Presentes', emoji: '🎁' },
  { value: 'clock', label: 'Tempo livre', emoji: '⏰' },
  { value: 'coffee', label: 'Café/Bar', emoji: '☕' },
  { value: 'tShirt', label: 'Roupas', emoji: '👕' },
  { value: 'doctor', label: 'Farmácia', emoji: '💊' },
  { value: 'bulb', label: 'Casa', emoji: '💡' },
  { value: 'dumbbell', label: 'Fitness', emoji: '💪' },
  { value: 'water', label: 'Água/Refrigerante', emoji: '💧' },
  { value: 'baby', label: 'Bebê', emoji: '👶' },
  { value: 'pets', label: 'Animais', emoji: '🐾' },
  { value: 'fire', label: 'Combustível', emoji: '⛽' },
  { value: 'bank', label: 'Bancos', emoji: '🏦' },
  { value: 'credit-card', label: 'Cartão', emoji: '💳' },
  { value: 'piggy-bank', label: 'Poupança', emoji: '🐖' },
  { value: 'chart-line', label: 'Investimentos', emoji: '📈' },
  { value: 'receipt', label: 'Serviços', emoji: '🧾' },
  { value: 'lightbulb', label: 'Ideias', emoji: '💡' },
]

// Color palette options
const COLOR_OPTIONS = [
  { value: '#DC3545', label: 'Vermelho' },
  { value: '#28A745', label: 'Verde' },
  { value: '#0C7DD8', label: 'Azul' },
  { value: '#FFC107', label: 'Amarelo' },
  { value: '#6F42C1', label: 'Roxo' },
  { value: '#17A2B8', label: 'Teal' },
  { value: '#E83E8C', label: 'Rosa' },
  { value: '#FD7E14', label: 'Laranja' },
  { value: '#6C757D', label: 'Cinza' },
] as const

export default function CategoryForm({ open, onClose, category }: CategoryFormProps) {
  const [name, setName] = useState(category?.name ?? '')
  const [type, setType] = useState<'income' | 'expense'>(category?.type ?? 'expense')
  const [icon, setIcon] = useState(category?.icon ?? '')
  const [color, setColor] = useState<string>(category?.color ?? '')
  const [parentCategory, setParentCategory] = useState<string>(category?.parent_category_id ?? '')

  const createCategory = useCreateCategory()
  const updateCategory = useUpdateCategory()
  const archiveCategory = useArchiveCategory()
  const queryClient = useQueryClient()

  // Get all categories for parent selection (excluding current one)
  const { data: categoriesData } = useCategories()
  const categories = categoriesData?.categories ?? []

  const isEditing = !!category
  const isSubmitting = createCategory.isPending || updateCategory.isPending

  // Reset form when category prop changes
  useEffect(() => {
    if (category) {
      setName(category.name)
      setType(category.type)
      setIcon(category.icon ?? '')
      setColor(category.color ?? '')
      setParentCategory(category.parent_category_id ?? '')
    } else {
      setName('')
      setType('expense')
      setIcon('')
      setColor('')
      setParentCategory('')
    }
  }, [category])

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault()

    if (!name.trim()) return

    if (isEditing && category) {
      updateCategory.mutate(
        {
          id: category.id,
          updates: {
            name: name.trim(),
            type,
            icon: icon || null,
            color: color || null,
            parent_category_id: parentCategory || null,
          },
        },
        {
          onSuccess: () => {
            onClose()
          },
        }
      )
    } else {
      createCategory.mutate(
        {
          name: name.trim(),
          type,
          icon: icon || null,
          color: color || null,
          parent_category_id: parentCategory || null,
        },
        {
          onSuccess: () => {
            onClose()
          },
        }
      )
    }
  }

  const handleDelete = () => {
    if (category && window.confirm('Deseja realmente excluir esta categoria? Esta ação não pode ser desfeita e migrará as transações para "Outros".')) {
      archiveCategory.mutate(category.id, {
        onSuccess: () => {
          onClose()
        },
      })
    }
  }

  // Filter parent categories: exclude current category and same type
  const availableParents = categories.filter(c => c.id !== category?.id && c.type === type && !c.parent_category_id)

  if (!open) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-[var(--color-surface)] rounded-xl shadow-xl w-full max-w-lg mx-4 max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-4 border-b border-[var(--color-border)]">
          <h3 className="text-lg font-semibold text-[var(--color-text)]">
            {isEditing ? 'Editar categoria' : 'Nova categoria'}
          </h3>
          <button
            onClick={onClose}
            className="rounded-full p-1 hover:bg-[var(--color-surface-subtle)] transition-colors"
          >
            <svg xmlns="http://www.w3.org/24/svg" className="h-5 w-5 text-[var(--color-text-muted)]" viewBox="0 0 24 24" fill="currentColor">
              <path fillRule="evenodd" d="M18 12a.75.75 0 01-.75.75H12v4.75a.75.75 0 01-1.5 0V12.75H4.75a.75.75 0 010-1.5h4.75V6.25a.75.75 0 011.5 0v4.75h4.75a.75.75 0 01.75.75z" clipRule="evenodd" />
            </svg>
          </button>
        </div>
        <div className="p-4 overflow-y-auto">
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Name */}
            <div>
              <label className="block text-sm font-medium text-[var(--color-text)] mb-1">
                Nome da categoria *
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Ex: Alimentação, Salário, Transporte..."
                className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-sm text-[var(--color-text)] placeholder-[var(--color-text-muted)] focus:border-[var(--color-brand-400)] focus:ring-1 focus:ring-[var(--color-brand-400)]"
                maxLength={50}
                required
              />
            </div>

            {/* Type */}
            <div>
              <label className="block text-sm font-medium text-[var(--color-text)] mb-1">
                Tipo
              </label>
              <div className="grid grid-cols-2 gap-2">
                {CATEGORY_TYPES.map((t) => (
                  <button
                    key={t.value}
                    type="button"
                    onClick={() => setType(t.value)}
                    className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${
                      type === t.value
                        ? 'bg-[var(--color-brand-500)] text-white border-[var(--color-brand-500)]'
                        : 'bg-[var(--color-surface)] border-[var(--color-border)] text-[var(--color-text)] hover:bg-[var(--color-surface-subtle)]'
                    }`}
                  >
                    <span className="text-lg">{t.icon}</span>
                    <span>{t.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Icon */}
            <div>
              <label className="block text-sm font-medium text-[var(--color-text)] mb-1">
                Ícone
              </label>
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                {ICON_OPTIONS.map((i) => (
                  <button
                    key={i.value}
                    type="button"
                    onClick={() => setIcon(i.value)}
                    className={`flex flex-col items-center gap-1 rounded-lg border p-2 text-xs transition-all ${
                      icon === i.value
                        ? 'border-[var(--color-brand-500)] bg-[var(--color-surface)]'
                        : 'border-[var(--color-border)] bg-[var(--color-surface)] hover:bg-[var(--color-surface-subtle)]'
                    }`}
                    title={i.label}
                  >
                    <span className="text-lg">{i.emoji}</span>
                    <span className="text-[var(--color-text-muted)]">{i.label}</span>
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() => setIcon('')}
                  className={`flex flex-col items-center gap-1 rounded-lg border p-2 text-xs transition-all ${
                    !icon
                      ? 'border-[var(--color-brand-500)] bg-[var(--color-surface)]'
                      : 'border-[var(--color-border)] bg-[var(--color-surface)] hover:bg-[var(--color-surface-subtle)]'
                  }`}
                  title="Sem ícone"
                >
                  <span className="text-lg text-[var(--color-text-muted)]">-</span>
                  <span className="text-[var(--color-text-muted)]">Nenhum</span>
                </button>
              </div>
            </div>

            {/* Color */}
            <div>
              <label className="block text-sm font-medium text-[var(--color-text)] mb-1">
                Cor
              </label>
              <div className="flex flex-wrap gap-2">
                {COLOR_OPTIONS.map((c) => (
                  <button
                    key={c.value}
                    type="button"
                    onClick={() => setColor(c.value)}
                    className={`h-8 w-8 rounded-full border-2 transition-all ${
                      color === c.value ? 'border-[var(--color-brand-500)] scale-110' : 'border-[var(--color-border)]'
                    }`}
                    style={{ backgroundColor: c.value }}
                    title={c.label}
                  />
                ))}
                <button
                  type="button"
                  onClick={() => setColor('')}
                  className={`h-8 w-8 rounded-full border-2 flex items-center justify-center transition-all ${
                    !color ? 'border-[var(--color-brand-500)] bg-[var(--color-surface)]' : 'border-[var(--color-border)]'
                  }`}
                  title="Sem cor"
                >
                  <svg xmlns="http://www.w3.org/24/svg" className="h-4 w-4 text-[var(--color-text-muted)]" viewBox="0 0 24 24" fill="currentColor">
                    <path fillRule="evenodd" d="M18 12a.75.75 0 01-.75.75H12v4.75a.75.75 0 01-1.5 0V12.75H4.75a.75.75 0 010-1.5h4.75V6.25a.75.75 0 011.5 0v4.75h4.75a.75.75 0 01.75.75z" clipRule="evenodd" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Parent Category (only for sub-categories) */}
            {availableParents.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-[var(--color-text)] mb-1">
                  Sub-categoria de (opcional)
                </label>
                <select
                  value={parentCategory}
                  onChange={(e) => setParentCategory(e.target.value)}
                  className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-sm text-[var(--color-text)] focus:border-[var(--color-brand-400)] focus:ring-1 focus:ring-[var(--color-brand-400)]"
                >
                  <option value="">Categoria principal</option>
                  {availableParents.map((cat) => (
                    <option key={cat.id} value={cat.id}>
                      {cat.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Submit */}
            <div className="flex gap-3 justify-end pt-2">
              <button
                type="button"
                onClick={onClose}
                className="rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-4 py-2 text-sm font-medium text-[var(--color-text)] hover:bg-[var(--color-surface-subtle)] transition-colors"
              >
                Cancelar
              </button>
              {isEditing && (
                <button
                  type="button"
                  onClick={handleDelete}
                  className="rounded-lg border border-[var(--color-danger)] bg-[var(--color-surface)] px-4 py-2 text-sm font-medium text-[var(--color-danger)] hover:bg-[var(--color-danger-light)] transition-colors"
                >
                  Excluir
                </button>
              )}
              <button
                type="submit"
                disabled={isSubmitting || !name.trim()}
                className="rounded-lg bg-[var(--color-brand-500)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--color-brand-600)] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Salvando...' : isEditing ? 'Atualizar' : 'Criar'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}
